import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, CheckCircle2, XCircle, Trophy, User as UserIcon } from "lucide-react";

/**
 * Drawer showing all student attempts for a specific test.
 * Teacher can click into an attempt to see full breakdown, override score, publish/unpublish.
 */
export default function TeacherAttemptsDrawer({ test, onClose }) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // {attempt, test}
  const [overrideScore, setOverrideScore] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/teacher/tests/${test.id}/attempts`);
      setAttempts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [test.id]);

  const openAttempt = async (a) => {
    try {
      const { data } = await api.get(`/teacher/attempts/${a.id}`);
      setDetail(data);
      setOverrideScore("");
      setRemarks(data.attempt.remarks || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const togglePublish = async (publish) => {
    if (!detail) return;
    setSaving(true);
    try {
      const payload = { published: publish };
      if (overrideScore !== "" && !Number.isNaN(Number(overrideScore))) {
        payload.override_score = Number(overrideScore);
      }
      if (remarks) payload.remarks = remarks;
      await api.post(`/teacher/attempts/${detail.attempt.id}/publish`, payload);
      toast.success(publish ? "Result published" : "Result unpublished");
      // Refresh both list + detail
      const [{ data: list }, { data: dat }] = await Promise.all([
        api.get(`/teacher/tests/${test.id}/attempts`),
        api.get(`/teacher/attempts/${detail.attempt.id}`),
      ]);
      setAttempts(list);
      setDetail(dat);
      setOverrideScore("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-testid="attempts-drawer">
      <div className="fixed right-0 top-0 bottom-0 w-full sm:max-w-2xl bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-black text-lg text-slate-900">Evaluate: {test.title}</h2>
            <p className="text-xs text-slate-500">{attempts.length} submission{attempts.length === 1 ? "" : "s"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="attempts-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <p className="text-sm text-slate-500">Loading attempts...</p>
          ) : detail ? (
            <AttemptDetail
              data={detail}
              overrideScore={overrideScore}
              setOverrideScore={setOverrideScore}
              remarks={remarks}
              setRemarks={setRemarks}
              onBack={() => setDetail(null)}
              onPublish={() => togglePublish(true)}
              onUnpublish={() => togglePublish(false)}
              saving={saving}
            />
          ) : attempts.length === 0 ? (
            <p className="text-sm text-slate-500">No students have attempted this test yet.</p>
          ) : (
            <div className="space-y-2">
              {attempts.map((a) => (
                <button key={a.id} onClick={() => openAttempt(a)} data-testid={`attempt-row-${a.id}`}
                  className="w-full text-left p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 flex items-center justify-between transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-600 grid place-items-center">
                      <UserIcon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{a.student_name}</div>
                      <div className="text-xs text-slate-500">
                        {a.score}/{a.total} · {a.percent}%
                        {a.published === false && <span className="ml-2 text-amber-600 font-semibold">Unpublished</span>}
                        {a.published !== false && <span className="ml-2 text-emerald-600 font-semibold">Published</span>}
                      </div>
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${a.percent >= 70 ? "text-emerald-600" : a.percent >= 40 ? "text-amber-600" : "text-rose-600"}`}>
                    {a.percent}%
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AttemptDetail({ data, overrideScore, setOverrideScore, remarks, setRemarks, onBack, onPublish, onUnpublish, saving }) {
  const { attempt, test } = data;
  const published = attempt.published !== false;
  return (
    <div className="space-y-5" data-testid="attempt-detail">
      <button onClick={onBack} className="text-sm text-blue-600 hover:underline">← Back to attempts</button>

      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
        <div className="flex items-center gap-3">
          <Trophy className={`h-5 w-5 ${attempt.percent >= 70 ? "text-emerald-600" : attempt.percent >= 40 ? "text-amber-600" : "text-rose-600"}`} />
          <div>
            <div className="font-heading font-bold text-slate-900">{attempt.student_name}</div>
            <div className="text-xs text-slate-500">Submitted {new Date(attempt.submitted_at).toLocaleString()}</div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded-lg bg-white border border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Score</div>
            <div className="font-heading font-black text-xl text-slate-900" data-testid="attempt-score">{attempt.score}/{attempt.total}</div>
          </div>
          <div className="p-2 rounded-lg bg-white border border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Percentage</div>
            <div className="font-heading font-black text-xl text-blue-700" data-testid="attempt-percent">{attempt.percent}%</div>
          </div>
          <div className="p-2 rounded-lg bg-white border border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Status</div>
            <div className={`font-heading font-black text-sm ${published ? "text-emerald-600" : "text-amber-600"}`}>{published ? "PUBLISHED" : "UNPUBLISHED"}</div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-heading font-bold text-slate-900 mb-2">Answer sheet</h3>
        <div className="space-y-3">
          {test.questions.map((q, i) => {
            const br = attempt.breakdown?.[i];
            const chosenIdx = br?.chosen ?? -1;
            const correct = br?.is_correct;
            return (
              <div key={q.id || i} className="p-3 rounded-lg border border-slate-200" data-testid={`q-review-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-900 flex-1">Q{i + 1}. {q.text}</div>
                  {correct ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  {(q.options || []).map((opt, j) => {
                    const isCorrect = j === q.correct_index;
                    const isChosen = j === chosenIdx;
                    return (
                      <div key={j} className={`px-2 py-1.5 rounded-md ${
                        isCorrect ? "bg-emerald-50 text-emerald-800 font-semibold" :
                          isChosen ? "bg-rose-50 text-rose-800" : "bg-slate-50 text-slate-600"
                      }`}>
                        {isCorrect ? "✓ " : isChosen ? "✗ " : ""}{opt}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">Worth {q.points || 1} mark{(q.points || 1) === 1 ? "" : "s"}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
        <h3 className="font-heading font-bold text-slate-900">Teacher actions</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-slate-700">Override score (optional)</label>
            <input type="number" step="0.5" min="0" max={attempt.total} value={overrideScore}
              onChange={(e) => setOverrideScore(e.target.value)}
              data-testid="attempt-override"
              placeholder={`Currently ${attempt.score}/${attempt.total}`}
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            <p className="mt-1 text-[11px] text-slate-500">Use to adjust for subjective marks. Percentage will recompute automatically.</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Remarks</label>
            <input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="attempt-remarks"
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onPublish} disabled={saving} data-testid="attempt-publish"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60">
            <CheckCircle2 className="h-4 w-4" /> {saving ? "..." : "Publish result"}
          </button>
          {published && (
            <button onClick={onUnpublish} disabled={saving} data-testid="attempt-unpublish"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-60">
              <XCircle className="h-4 w-4" /> Unpublish
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
